import React from "react";
import { X, Printer } from "lucide-react";
import "./styles.css";

interface MatchData {
  nr: number;
  prediction: string;
  home: string;
  away: string;
  percentages?: { "1": number; X: number; "2": number };
  isPreliminary?: boolean;
  couponHidden?: boolean;
}

interface TicketData {
  operatorId: string;
  syndicateNumber: string;
  gameType: string;
  shares: number;
  round: string;
  system: string;
  serialNumber: string;
  stakePerShare: number;
  feePerShare: number;
  costPerShare: number;
  costTotal: number;
  matches: MatchData[];
}

export interface ReceiptMessage {
  type: string;
  ticketData: TicketData;
}

interface ReceiptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  receipt: ReceiptMessage | null;
}

export default function ReceiptDialog({
  isOpen,
  onClose,
  receipt,
}: ReceiptDialogProps) {
  if (!isOpen || !receipt) return null;

  const { ticketData } = receipt;

  return (
    <div className="dialog-overlay">
      <div className="dialog-content receipt-dialog">
        <div className="dialog-header">
          <div className="flex items-center space-x-2">
            <Printer className="icon-md" style={{ color: "#22c55e" }} />
            <h2 className="text-xl font-bold text-white">Store Receipt</h2>
          </div>
          <button onClick={onClose} className="dialog-close">
            <X className="icon-md" />
          </button>
        </div>

        <div className="receipt-body">
          {/* Ticket Info */}
          <div className="receipt-section">
            <h3 className="receipt-section-title">Ticket Info</h3>
            <div className="receipt-grid">
              <div className="receipt-field">
                <span className="receipt-label">Syndicate</span>
                <span className="receipt-value">{ticketData.syndicateNumber}</span>
              </div>
              <div className="receipt-field">
                <span className="receipt-label">Game Type</span>
                <span className="receipt-value">{ticketData.gameType || "—"}</span>
              </div>
              <div className="receipt-field">
                <span className="receipt-label">Round</span>
                <span className="receipt-value">{ticketData.round}</span>
              </div>
              <div className="receipt-field">
                <span className="receipt-label">System</span>
                <span className="receipt-value">{ticketData.system || "—"}</span>
              </div>
              <div className="receipt-field">
                <span className="receipt-label">Serial #</span>
                <span className="receipt-value">{ticketData.serialNumber}</span>
              </div>
              <div className="receipt-field">
                <span className="receipt-label">Shares</span>
                <span className="receipt-value">{ticketData.shares}</span>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="receipt-section">
            <h3 className="receipt-section-title">Cost Breakdown</h3>
            <div className="receipt-grid">
              <div className="receipt-field">
                <span className="receipt-label">Stake/Share</span>
                <span className="receipt-value">{ticketData.stakePerShare.toFixed(2)}</span>
              </div>
              <div className="receipt-field">
                <span className="receipt-label">Fee/Share</span>
                <span className="receipt-value">{ticketData.feePerShare.toFixed(2)}</span>
              </div>
              <div className="receipt-field">
                <span className="receipt-label">Cost/Share</span>
                <span className="receipt-value">{ticketData.costPerShare.toFixed(2)}</span>
              </div>
              <div className="receipt-field">
                <span className="receipt-label">Total Cost</span>
                <span className="receipt-value receipt-total">
                  {ticketData.costTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Matches */}
          <div className="receipt-section">
            <h3 className="receipt-section-title">Matches</h3>
            <div className="receipt-matches">
              {ticketData.matches.map((match) => (
                <div key={match.nr} className="receipt-match">
                  <div className="receipt-match-header">
                    <span className="receipt-match-nr">{match.nr}.</span>
                    <span className="receipt-match-teams">
                      {match.home} vs {match.away}
                    </span>
                    {match.isPreliminary && (
                      <span className="receipt-badge receipt-badge-preliminary">
                        Preliminary
                      </span>
                    )}
                    {match.couponHidden && (
                      <span className="receipt-badge receipt-badge-hidden">
                        Hidden
                      </span>
                    )}
                  </div>
                  <div className="receipt-match-details">
                    <span className="receipt-prediction">
                      {match.prediction || "—"}
                    </span>
                    {match.percentages && (
                      <span className="receipt-percentages">
                        1: {match.percentages["1"]}% | X: {match.percentages.X}% | 2:{" "}
                        {match.percentages["2"]}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
